import {
  computed,
  inject,
  Injectable,
  Injector,
  resource,
  signal,
  Signal,
  TemplateRef,
} from '@angular/core';
import {
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventDeleteEvent,
  MbscEventDragEvent,
  MbscResource,
} from '@mobiscroll/angular';
import {
  AdditionalData,
  CalendarViewType,
  DetailEmployee,
  LoadRosterStructureParams,
  RosterContent,
  RosterEventRepository,
  RosterService,
  RosterStructureRepository,
  Structure,
} from '@softapps/pms/roster-core';
import { addDays, IsoDate, toIsoDate, toIsoDateTime } from '@softline/core';
import { handleRequestError } from '@softline/application';
import { MessageBarService, modalFn, translator } from '@softline/ui-core';
import { EventArtifactDialogComponent } from '../dialogs/event-artifact-dialog/event-artifact-dialog.component';
import { ArtifactDialogData, ArtifactDialogResult } from '../types/artifact-dialog';
import {
  EventDialogComponent,
  EventDialogResult,
} from '../dialogs/event-dialog/event-dialog.component';
import { RosterDeliveryContent } from '../types/roster-delivery-content';
import { DeliveryNoteTabName } from '../types/delivery-note-tab-name';
import { DeliveryNotePlanMode } from '../types/delivery-note-plan-type';
import { RosterDeliveryContentPayload } from '../types/roster-delivery-content-payload';
import { RosterDeliveryStructure } from '../types/roster-delivery-structure';
import { RosterDeliveryResourceType } from '../types/roster-delivery-resource-type';
import { DeliveryNoteRosterMenuBarActionsRepository } from '../repositories/delivery-note-roster-menu-bar-actions.repository';
import { DeliveryNoteRosterEventRepository } from '../repositories/delivery-note-roster-event.repository';
import { DeliveryNoteRosterArtifactsService } from './delivery-note-roster-artifacts.service';

export interface ContextMenuAction {
  action: string;
  label: string;
  icon: string;
  danger?: boolean;
}

@Injectable()
export class DeliveryNoteRosterService extends RosterService<RosterContent> {
  readonly #structureRepository = inject<
    RosterStructureRepository<RosterDeliveryStructure[]>
  >(RosterStructureRepository);
  readonly #eventRepository = inject(
    RosterEventRepository<RosterContent, AdditionalData>
  );
  readonly #messageBar = inject(MessageBarService);
  readonly #injector = inject(Injector);
  readonly #t = translator();
  readonly #modal = modalFn();
  readonly #menuBarActionsRepository = inject(DeliveryNoteRosterMenuBarActionsRepository);
  readonly #artifactsService = inject(DeliveryNoteRosterArtifactsService);

  readonly #contextMenuEvent = signal<RosterDeliveryContent | null>(null);
  readonly contextMenuEventDate = signal<Date | null>(null);
  readonly contextMenuResource = signal<number | string | null>(null);

  /** The action list the DnRosterMenuBar's context-menu template renders. Set
      by onEventRightClick / onCellRightClick; the shared calendar opens the
      resolved template. */
  readonly contextMenuActions = signal<ContextMenuAction[]>([]);

  /** The soft-ds-context-menu template, owned by DnRosterMenuBar. */
  readonly contextMenu = signal<TemplateRef<unknown> | undefined>(undefined);

  readonly #beginDateSource = signal<Signal<string | null>>(
    signal(toIsoDate(new Date()))
  );

  readonly #calendarViewTypeSource = signal<Signal<CalendarViewType>>(
    signal<CalendarViewType>('week')
  );

  readonly #resourceFilterStringSource = signal<Signal<string>>(signal(''));

  override readonly startOfPeriod: Signal<Date> = computed(() => {
    const dateStr = this.#beginDateSource()();
    const viewType = this.#calendarViewTypeSource()();
    if (!dateStr) return new Date();
    const date = new Date(dateStr);
    switch (viewType) {
      case 'day':
        return date;
      case 'week':
      case 'workWeek': {
        const daysToStartOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
        const start = new Date(date);
        start.setDate(date.getDate() - daysToStartOfWeek);
        return start;
      }
      case 'month':
        return new Date(date.getFullYear(), date.getMonth(), 1);
      default:
        return date;
    }
  });

  override readonly endOfPeriod: Signal<Date> = computed(() => {
    const dateStr = this.#beginDateSource()();
    const viewType = this.#calendarViewTypeSource()();
    if (!dateStr) return new Date();
    const date = new Date(dateStr);
    switch (viewType) {
      case 'day':
        return new Date(date.setHours(23, 59, 59));
      case 'week':
      case 'workWeek': {
        const daysToEndOfWeek = date.getDay() === 0 ? 0 : 7 - date.getDay();
        const end = new Date(date);
        end.setDate(date.getDate() + daysToEndOfWeek);
        return end;
      }
      case 'month':
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
      default:
        return date;
    }
  });

  readonly #deliveryStructure = resource({
    loader: async () => {
      try {
        return await this.#structureRepository.load({} as LoadRosterStructureParams);
      } catch {
        return [];
      }
    },
  });
  override readonly structure: Signal<Structure | undefined> = computed(() => {
    const items = this.#deliveryStructure.value();
    if (!items?.length) return undefined;
    return { id: 0, begintime: '06:00', endtime: '22:00', departements: [] };
  });

  override readonly calendarContentLoading: Signal<boolean> = computed(
    () => this.#deliveryStructure.isLoading() || this.#events.isLoading()
  );

  readonly #allResources: Signal<MbscResource[]> = computed(() => {
    const items = this.#deliveryStructure.value();
    if (!items) return [];

    const grouped = new Map<RosterDeliveryResourceType, RosterDeliveryStructure[]>();
    for (const item of items) {
      const group = grouped.get(item.resourceType) ?? [];
      group.push(item);
      grouped.set(item.resourceType, group);
    }

    return Array.from(grouped.entries()).map(([type, children]) => ({
      id: `group_${type}`,
      name: this.#t(`#DN_ROSTER.RESOURCE_TYPES.${type}`)(),
      eventCreation: false,
      children: children.map((item) => ({
        id: item.id,
        name: item.name,
        key: item.key,
        resourceType: item.resourceType,
        eventCreation: true,
      })),
    }));
  });

  override readonly resources: Signal<MbscResource[]> = computed(() => {
    const term = this.#resourceFilterStringSource()().trim().toLowerCase();
    const groups = this.#allResources();
    if (!term) return groups;

    return groups
      .map((group) => ({
        ...group,
        children: (group.children ?? []).filter(
          (child) =>
            child.name?.toLowerCase().includes(term) ||
            (child['key'] as string | undefined)?.toLowerCase().includes(term)
        ),
      }))
      .filter((group) => (group.children?.length ?? 0) > 0);
  });

  readonly #events = resource({
    params: () => {
      const leafResources = this.resources().flatMap((r) => r.children ?? []);
      const begin = toIsoDate(this.startOfPeriod());
      const end = toIsoDate(this.endOfPeriod());
      if (!leafResources.length) return undefined;
      return { resources: leafResources, begin, end };
    },
    loader: async ({ params }) => {
      if (!params) return [];
      const results = await Promise.all(
        params.resources.map(async (resource) => {
          try {
            return await (
              this.#eventRepository as DeliveryNoteRosterEventRepository
            ).loadEvents(0, params.begin, params.end, String(resource.id));
          } catch {
            return [];
          }
        })
      );
      const allEventsMap = new Map<string, RosterDeliveryContent>();
      results.forEach((events, i) => {
        const resource = params.resources[i];
        for (const e of events) {
          if (e.id == null) continue;
          const event = e as unknown as RosterDeliveryContent;
          allEventsMap.set(`${resource.id}_${e.id}`, {
            ...event,
            resource: resource.id,
            editable: !event.additionalData.exported,
          });
        }
      });
      return Array.from(allEventsMap.values());
    },
  });

  readonly hideDeletedEvents = signal<boolean>(false);

  override readonly events: Signal<RosterContent[]> = computed(() => {
    const events = (this.#events.value() ?? []) as unknown as RosterDeliveryContent[];
    return (this.hideDeletedEvents()
      ? events.filter((e) => !e.additionalData?.deleted)
      : events) as unknown as RosterContent[];
  });

  toggleHideDeletedEvents(): void {
    this.hideDeletedEvents.update((v) => !v);
  }

  readonly #selectedEventIds = signal<Set<string | number>>(new Set());
  override readonly selectedEventIds = computed(() =>
    Array.from(this.#selectedEventIds())
  );
  override readonly employeeDetail = signal<Map<number | string, DetailEmployee> | null>(
    null
  );
  override readonly calendarDetails = signal<Signal<'compact' | 'all'>>(signal('all'));

  override registerDepartmentHead(): void {
    /* no-op */
  }
  override registerBeginDate(value: Signal<string>): void {
    this.#beginDateSource.set(value);
  }
  override registerCalenderViewType(value: Signal<CalendarViewType>): void {
    this.#calendarViewTypeSource.set(value);
  }
  override registerPlanMonthRange(): void {
    /* no-op */
  }
  override registerCalendarColumnWidth(): void {
    /* no-op */
  }
  override registerCalendarDetails(value: Signal<'compact' | 'all'>): void {
    this.calendarDetails.set(value);
  }
  override registerEventTypesFilter(): void {
    /* no-op */
  }
  override registerResourceFilterString(value: Signal<string>): void {
    this.#resourceFilterStringSource.set(value);
  }
  override handleContextMenuAction(action: string): void {
    switch (action) {
      case 'edit': {
        const event = this.#contextMenuEvent();
        if (event) this.#openEditDialog(event);
        break;
      }
      case 'delete': {
        void this.deleteSelectedEvents();
        break;
      }
      case 'create':
        this.#openCreateDialog();
        break;
      case 'exportDay':
        void this.#exportDay();
        break;
    }
    this.#closeContextMenu();
  }
  override refreshEvents(): void {
    this.#events.reload();
  }
  override loadEmployeeContent(): Promise<void> {
    return Promise.resolve();
  }

  private numericResourceId(compositeId: string | number): number {
    return Number(String(compositeId).split('_')[1]);
  }

  override async onEventCreate(e: MbscEventCreateEvent): Promise<void> {
    const start = e.event.start as Date;
    const resourceId = Array.isArray(e.event.resource)
      ? e.event.resource[0]
      : e.event.resource;
    const resourceType =
      resourceId != null
        ? (String(resourceId).split('_')[0] as RosterDeliveryResourceType)
        : null;

    const date = toIsoDate(start);
    const viewType = this.#calendarViewTypeSource()();
    let startString: string;
    let endString: string;
    if (viewType === 'day') {
      const end = e.event.end as Date;
      startString = toIsoDateTime(start);
      endString = toIsoDateTime(end);
    } else {
      startString = `${date}T06:00:00.000`;
      endString = `${date}T23:59:59.000`;
    }

    const payload: RosterDeliveryContentPayload = {
      vonzeit: startString,
      biszeit: endString,
      datum: date,
      ...(resourceType === RosterDeliveryResourceType.Fahrzeug
        ? { idfahrzeug: this.numericResourceId(resourceId ?? '') }
        : { idvkforg: this.numericResourceId(resourceId ?? '') }),
      // vkftourfolge: 0,
      // abholpunkt: 0,
      bemerkung: '',
      divers: [
        {
          tabname: e.event['_tabname'] as DeliveryNoteTabName,
          idtab: e.event['id'] as number,
        },
      ],
    };

    try {
      await this.#eventRepository.createEvent(
        payload as unknown as Partial<RosterContent>
      );
      this.#events.reload();
      this.reloadArtifacts(e.event['_tabname'] as DeliveryNoteTabName);
      this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.CREATE_SUCCESS'));
    } catch (err) {
      handleRequestError(err, this.#injector, { show: true });
      this.#events.reload();
    }
  }

  override onEventClick(args: MbscEventClickEvent): Promise<void> {
    const event = args.event as unknown as RosterDeliveryContent;
    if (!event.id) return Promise.resolve();
    if (event.additionalData?.deleted === true) return Promise.resolve();

    const mouseEvent = args.domEvent as MouseEvent;
    const multiSelectKey = mouseEvent.ctrlKey || mouseEvent.metaKey;

    if (multiSelectKey) {
      const selection = new Set(this.#selectedEventIds());
      if (selection.has(event.id)) {
        selection.delete(event.id);
      } else {
        selection.add(event.id);
      }
      this.#selectedEventIds.set(selection);
    } else {
      const current = this.#selectedEventIds();
      this.#selectedEventIds.set(
        current.size === 1 && current.has(event.id) ? new Set() : new Set([event.id])
      );
    }
    return Promise.resolve();
  }
  override async onEventRightClick(
    args: MbscEventClickEvent,
    __: unknown
  ): Promise<void> {
    const event = args.event as unknown as RosterDeliveryContent;
    const isExported = event.additionalData?.exported;
    const isFullyPlanned = event.additionalData?.mode === DeliveryNotePlanMode.Detail;
    const isDeleted = event.additionalData?.deleted;

    const mouseEvent = args.domEvent as MouseEvent;
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    if (event.id !== undefined) {
      const selection = this.#selectedEventIds();
      if (!selection.has(event.id)) {
        this.#selectedEventIds.set(new Set(selection).add(event.id));
      }
    }

    if ((isExported && isFullyPlanned) || isDeleted) {
      this.#contextMenuEvent.set(null);
      this.contextMenuActions.set([]);
      return;
    }

    const isArtifact = (event.additionalData?.additionalDetail?.length ?? 0) > 0;
    const trashLabel = !isArtifact ? 'Löschen' : 'Entfernen';

    const actions: ContextMenuAction[] = [];
    if (!isExported) {
      actions.push({ action: 'edit', label: 'Bearbeiten', icon: 'fa-regular fa-pen' });
    }
    actions.push({
      action: 'delete',
      label: trashLabel,
      icon: 'fa-regular fa-trash',
      danger: true,
    });

    this.#contextMenuEvent.set(event);
    this.contextMenuActions.set(actions);
  }

  override async onEventDoubleClick(
    args: MbscEventClickEvent,
    __: unknown
  ): Promise<void> {
    const event = args.event as unknown as RosterDeliveryContent;
    await this.#openEditDialog(event);
  }

  override onCellClick(_: MbscCellClickEvent, __: unknown): Promise<void> {
    this.#selectedEventIds.set(new Set());
    return Promise.resolve();
  }

  override async onCellRightClick(args: MbscCellClickEvent, __: unknown): Promise<void> {
    const mouseEvent = args.domEvent as MouseEvent;
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    this.#selectedEventIds.set(new Set());
    this.#contextMenuEvent.set(null);
    this.contextMenuEventDate.set(args.date as Date);
    this.contextMenuResource.set(args.resource as string | number);
    this.contextMenuActions.set([
      { action: 'create', label: 'Neuer Eintrag', icon: 'fa-regular fa-plus' },
      {
        action: 'exportDay',
        label: 'Tag abgleichen',
        icon: 'fa-regular fa-arrows-rotate',
      },
    ]);
  }

  // No actions => no menu (the right-click handler suppressed it). The shared
  // calendar's openContextMenu no-ops on an undefined template.
  override resolveEventMenu(): TemplateRef<unknown> | undefined {
    return this.contextMenuActions().length > 0 ? this.contextMenu() : undefined;
  }

  override resolveCalendarMenu(): TemplateRef<unknown> | undefined {
    return this.contextMenuActions().length > 0 ? this.contextMenu() : undefined;
  }

  override async onCellDoubleClick(args: MbscCellClickEvent, __: unknown): Promise<void> {
    const date = args.date as Date;
    const datum = toIsoDate(date);
    const resourceId = String(args.resource);
    const resourceType = resourceId.split('_')[0] as RosterDeliveryResourceType;

    const result = await this.#modal({
      component: EventDialogComponent,
      title: this.#t('#DN_ROSTER.CREATE_EVENT'),
      data: { datum, resourceType },
    });

    if (!result || result === 'DISMISSED' || result === 'CANCEL') return;

    const r = result as EventDialogResult;

    const payload: RosterDeliveryContentPayload = {
      vonzeit: r.vonzeit,
      biszeit: r.biszeit,
      datum: r.datum,
      ...(resourceType === RosterDeliveryResourceType.Fahrzeug
        ? { idfahrzeug: this.numericResourceId(resourceId) }
        : { idvkforg: this.numericResourceId(resourceId) }),
      bemerkung: r.bemerkung,
      title: r.title,
      vkftourfolge: r.tour,
    };

    try {
      await this.#eventRepository.createEvent(
        payload as unknown as Partial<RosterContent>
      );
      this.#events.reload();
      this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.CREATE_SUCCESS'));
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      this.#events.reload();
    }
  }

  override onEventDragStart(_: MbscEventDragEvent, __: unknown): Promise<void> {
    return Promise.resolve();
  }

  override async onEventDragEnd(args: MbscEventDragEvent): Promise<void> {
    if (!args.event['additionalData']) return;

    const start = args.event.start as Date;
    const end = args.event.end as Date;

    const startUTC = new Date(
      Date.UTC(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        start.getHours(),
        start.getMinutes(),
        start.getSeconds()
      )
    );
    const endUTC = new Date(
      Date.UTC(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        end.getHours(),
        end.getMinutes(),
        end.getSeconds()
      )
    );

    const startString = startUTC.toISOString().replace('Z', '');
    const endString = endUTC.toISOString().replace('Z', '');

    const additionalData = args.event[
      'additionalData'
    ] as RosterDeliveryContent['additionalData'];
    const resourceId = Array.isArray(args.event.resource)
      ? args.event.resource[0]
      : args.event.resource;
    const resourceType =
      resourceId != null
        ? (String(resourceId).split('_')[0] as RosterDeliveryResourceType)
        : null;

    const payload: RosterDeliveryContentPayload = {
      vonzeit: startString,
      biszeit: endString,
      datum: toIsoDate(start),
      ...(resourceType === RosterDeliveryResourceType.Fahrzeug
        ? { idfahrzeug: this.numericResourceId(resourceId ?? '') }
        : { idvkforg: this.numericResourceId(resourceId ?? '') }),
      vkftourfolge: additionalData?.tour,
      //abholpunkt: 0,
      bemerkung: additionalData?.comment ?? '',
      title: additionalData?.title ?? '',
      divers: (additionalData?.additionalDetail ?? []).map((d) => ({
        tabname: d.tabName as DeliveryNoteTabName,
        idtab: d.tabId,
      })),
    };

    try {
      await this.#eventRepository.updateEvent({
        id: args.event.id,
        ...payload,
      });
      this.#events.reload();
      this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.UPDATE_SUCCESS'));
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      this.#events.reload();
    }
  }

  override onEventDelete(_: MbscEventDeleteEvent, __: unknown): boolean {
    return false;
  }

  override async deleteEvents(events: RosterContent[]): Promise<boolean> {
    try {
      await this.#eventRepository.deleteEvents(events);
      this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.DELETE_SUCCESS'));

      const exported = (events[0] as unknown as RosterDeliveryContent).additionalData
        ?.exported;
      if (!exported) {
        const deletedIds = new Set(events.map((e) => e.id));
        this.#events.update((current) =>
          (current ?? []).filter((e) => !deletedIds.has(e.id))
        );
      } else {
        this.#events.reload();
      }

      const isArtifact =
        ((events[0] as unknown as RosterDeliveryContent).additionalData?.additionalDetail
          ?.length ?? 0) > 0;
      if (isArtifact) {
        this.reloadArtifacts();
      }

      return true;
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      return false;
    }
  }

  override async deleteSelectedEvents(): Promise<boolean> {
    const selected = this.events().filter(
      (e) => e.id !== undefined && this.selectedEventIds().includes(e.id)
    );
    const deletable = selected.filter((e) => {
      const ad = (e as unknown as RosterDeliveryContent).additionalData;
      return !((ad?.exported && ad?.mode === DeliveryNotePlanMode.Detail) || ad?.deleted);
    });
    if (deletable.length < selected.length) {
      this.#messageBar.warning(this.#t('#DN_ROSTER.MESSAGES.DELETE_SOME_SKIPPED')());
    }
    if (deletable.length === 0) return false;
    return this.deleteEvents(deletable);
  }

  override onKeyDown(_: KeyboardEvent): Promise<void> {
    return Promise.resolve();
  }

  private reloadArtifacts(tabname?: DeliveryNoteTabName): void {
    if (!tabname) {
      this.#artifactsService.reloadOrders();
      this.#artifactsService.reloadPurchaseOrders();
    }
    if (tabname === DeliveryNoteTabName.Order) {
      this.#artifactsService.reloadOrders();
    } else if (tabname === DeliveryNoteTabName.PurchaseOrder) {
      this.#artifactsService.reloadPurchaseOrders();
    }
  }

  async importData(): Promise<void> {
    await this.#menuBarActionsRepository.import();
    this.#events.reload();
  }

  async exportData(beginDate: string, endDate: string): Promise<void> {
    await this.#menuBarActionsRepository.export(beginDate, endDate);
    this.#events.reload();
  }

  async #exportDay(): Promise<void> {
    const date = this.contextMenuEventDate();
    if (!date) return;
    const datum = toIsoDate(date);
    try {
      await this.exportData(datum, datum);
      this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.EXPORT_DAY_SUCCESS'));
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async sendMail(): Promise<{
    succeeded: number;
    failures: { id: number; error: unknown }[];
  }> {
    const selected = (this.selectedEvents() as unknown as RosterDeliveryContent[]).filter(
      (e) => e.additionalData?.exported
    );
    const results = await Promise.all(
      selected.map(async (event) => {
        try {
          await this.#menuBarActionsRepository.sendMail(event.additionalData.id);
          return { ok: true as const, id: event.additionalData.id };
        } catch (error) {
          return { ok: false as const, id: event.additionalData.id, error };
        }
      })
    );
    const failures = results.filter(
      (r): r is { ok: false; id: number; error: unknown } => !r.ok
    );
    return { succeeded: results.length - failures.length, failures };
  }

  async #openEditDialog(event: RosterDeliveryContent): Promise<void> {
    if (!event.resource) return;

    const startDate =
      event.start instanceof Date ? event.start : new Date(event.start as string);
    const datum = toIsoDate(startDate);
    const resourceId = Array.isArray(event.resource) ? event.resource[0] : event.resource;
    const resourceType =
      resourceId != null
        ? (String(resourceId).split('_')[0] as RosterDeliveryResourceType)
        : null;
    const resourceEntry = this.#deliveryStructure
      .value()
      ?.find((v) => v.id === resourceId);
    const resourceName =
      (resourceEntry?.name?.length ?? 0) > 0
        ? resourceEntry?.name
        : (resourceEntry?.key ?? '');

    const isArtifact = (event.additionalData?.additionalDetail?.length ?? 0) > 0;

    if (!isArtifact) {
      const timeFrom = (iso: string | null | undefined): string =>
        iso?.split('T')[1]?.substring(0, 5) ?? '';

      const editResult = await this.#modal({
        component: EventDialogComponent,
        title: this.#t('#DN_ROSTER.EDIT_EVENT'),
        data: {
          datum,
          resourceType,
          resourceName,
          mode: 'edit',
          vonzeit: timeFrom(event.additionalData?.beginTime),
          biszeit: timeFrom(event.additionalData?.endTime),
          bemerkung: event.additionalData?.comment ?? '',
          tour: event.additionalData?.tour ?? '',
          title: event.additionalData?.title ?? '',
        },
      });

      if (!editResult || editResult === 'DISMISSED' || editResult === 'CANCEL') return;

      if (editResult === 'DELETE') {
        await this.deleteEvents([event as unknown as RosterContent]);
        return;
      }

      const r = editResult as EventDialogResult;
      const editPayload: RosterDeliveryContentPayload = {
        vonzeit: r.vonzeit,
        biszeit: r.biszeit,
        datum: r.datum,
        ...(resourceType === RosterDeliveryResourceType.Fahrzeug
          ? { idfahrzeug: this.numericResourceId(resourceId ?? '') }
          : { idvkforg: this.numericResourceId(resourceId ?? '') }),
        vkftourfolge: r.tour,
        //abholpunkt: 0,
        bemerkung: r.bemerkung,
        title: r.title,
        divers: [],
      };

      try {
        await this.#eventRepository.updateEvent({
          id: event.id,
          ...editPayload,
        } as unknown as RosterContent);
        this.#events.reload();
        this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.UPDATE_SUCCESS'));
      } catch (e) {
        handleRequestError(e, this.#injector, { show: true });
      }
      return;
    }

    const resource = Array.isArray(event.resource) ? event.resource : [event.resource];
    const artifact = event.additionalData?.additionalDetail?.[0];
    const titlePrefix = artifact?.tabName === DeliveryNoteTabName.Order ? 'A' : 'B';
    const mode = event.additionalData.mode ?? null;
    const exported = event.additionalData.exported;
    const isNotDeletable =
      (exported && event.additionalData?.mode === DeliveryNotePlanMode.Detail) ||
      event.additionalData?.deleted;
    const artifactTabTitle =
      artifact?.tabName === DeliveryNoteTabName.Order ? 'Auftrag' : 'Bestellung';

    const result = await this.#modal({
      component: EventArtifactDialogComponent,
      title: artifact?.title ? `${titlePrefix}-${artifact.title}` : (titlePrefix ?? ''),
      data: {
        event: event as unknown as RosterContent,
        resource,
        mode,
        isReadonly: exported,
        denyDelete: isNotDeletable,
        artifactTabTitle,
      } as ArtifactDialogData,
    });

    if (!result || result === 'DISMISSED' || result === 'CANCEL') return;

    if (result === 'DELETE') {
      await this.deleteEvents([event as unknown as RosterContent]);
      return;
    }

    const dialogResult = result as ArtifactDialogResult;
    const updatedAd = dialogResult.eventPayload
      .additionalData as unknown as RosterDeliveryContent['additionalData'];

    const updatedDatum = (updatedAd as unknown as { datum?: string }).datum ?? datum;
    const vonzeitTime = (updatedAd.beginTime ?? '00:00').substring(0, 5);
    const biszeitTime = (updatedAd.endTime ?? '00:00').substring(0, 5);
    const biszeitDatum =
      biszeitTime <= vonzeitTime ? addDays(updatedDatum as IsoDate, 1) : updatedDatum;

    const artifactPayload: RosterDeliveryContentPayload = {
      vonzeit: `${updatedDatum}T${vonzeitTime}:00`,
      biszeit: `${biszeitDatum}T${biszeitTime}:00`,
      datum: updatedDatum,
      ...(resourceType === RosterDeliveryResourceType.Fahrzeug
        ? { idfahrzeug: this.numericResourceId(resourceId ?? '') }
        : { idvkforg: this.numericResourceId(resourceId ?? '') }),
      bemerkung: updatedAd.comment ?? '',
      divers: (event.additionalData.additionalDetail ?? []).map((d) => ({
        tabname: d.tabName as DeliveryNoteTabName,
        idtab: d.tabId,
      })),
      vkftourfolge: updatedAd.tour,
    };

    try {
      await this.#eventRepository.updateEvent({
        id: event.id,
        ...artifactPayload,
      } as unknown as RosterContent);
      this.#events.reload();
      this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.UPDATE_SUCCESS'));
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async #openCreateDialog(): Promise<void> {
    const date = this.contextMenuEventDate();
    const resourceId = String(this.contextMenuResource());
    if (!date || !resourceId) return;

    const datum = toIsoDate(date);
    const resourceType = String(resourceId).split('_')[0] as RosterDeliveryResourceType;

    const result = await this.#modal({
      component: EventDialogComponent,
      title: this.#t('#DN_ROSTER.CREATE_EVENT'),
      data: { datum, resourceType },
    });

    if (!result || result === 'DISMISSED' || result === 'CANCEL') return;

    const r = result as EventDialogResult;
    const payload: RosterDeliveryContentPayload = {
      vonzeit: r.vonzeit,
      biszeit: r.biszeit,
      datum: r.datum,
      ...(resourceType === RosterDeliveryResourceType.Fahrzeug
        ? { idfahrzeug: this.numericResourceId(resourceId) }
        : { idvkforg: this.numericResourceId(resourceId) }),
      bemerkung: r.bemerkung,
      title: r.title,
      vkftourfolge: r.tour,
    };

    try {
      await this.#eventRepository.createEvent(
        payload as unknown as Partial<RosterContent>
      );
      this.#events.reload();
      this.#messageBar.success(this.#t('#DN_ROSTER.MESSAGES.CREATE_SUCCESS'));
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      this.#events.reload();
    }
  }

  #closeContextMenu(): void {
    this.closeContextMenuOverlay?.();
    this.#contextMenuEvent.set(null);
    this.contextMenuEventDate.set(null);
    this.contextMenuResource.set(null);
    this.contextMenuActions.set([]);
  }
}
